/* tslint:disable:no-console */
import { polyfill } from 'es6-promise';
import { flatbuffers } from 'flatbuffers';
import { v4 as uuid } from 'uuid';
import 'whatwg-fetch';

import { ServiceAPIFB } from './Protocol/EndeavourProtocol';
import { ResourceModel } from './ResourceModel';

polyfill();

/**
 * Implementation of a WebClientRequester that uses the fetch API protocols
 */
export class FetchRequester {
  private readonly url: string;

  public constructor(url: string) {
    if (fetch === undefined || Request === undefined) {
      throw new Error('The fetch API is not defined in this browser. Try adding a polyfill library like "whatwg-fetch"');
    }
    this.url = url;
  }

  public postSpans(byteArray: Uint8Array): void {
    // Convert to (signed) int8, since this is how Java represents a byte on server side
    const byteInt8Array = Int8Array.from(byteArray);

    const spansUrl = `${this.url}/spans/v2`;
    const init: RequestInit = {
      method: 'POST',
      headers: this.getHeaders(),
      mode: 'cors',
      credentials: 'same-origin',
      body: byteInt8Array,
    };

    this.sendRequest(spansUrl, init);
  }

  public async getPipelineConfigurationReply(): Promise<ServiceAPIFB.GetPipelineConfigurationReply> {
    try {
      const builder = new flatbuffers.Builder(1024);
      const resourceModel = new ResourceModel();
      const resourcesOffset = resourceModel.buildResource(builder);
      const getPipelineConfigurationRequest = ServiceAPIFB.GetPipelineConfigurationRequest.createGetPipelineConfigurationRequest(
        builder,
        resourcesOffset
      );
      builder.finish(getPipelineConfigurationRequest);

      const uInt8getPipelineConfigurationRequest: Uint8Array = builder.asUint8Array();
      // Convert to (signed) int8, since this is how Java represents a byte on server side
      const byteInt8Array = Int8Array.from(uInt8getPipelineConfigurationRequest);
      const configurationUrl = `${this.url}/configuration/v1`;

      let response = await fetch(configurationUrl, {
        method: 'POST',
        headers: this.getHeaders(),
        mode: 'cors',
        credentials: 'same-origin',
        body: byteInt8Array,
      });

      response = response.clone();

      if (!response.ok) {
        throw Error(response.statusText);
      }

      const arrayBuffer = await response.arrayBuffer();
      const arrayBytes = new Uint8Array(arrayBuffer);
      const fbArr = new flatbuffers.ByteBuffer(arrayBytes);
      return ServiceAPIFB.GetPipelineConfigurationReply.getRootAsGetPipelineConfigurationReply(fbArr);
    } catch (err) {
      throw err;
    }
  }

  public sendRequest(path: string, init: RequestInit): void {
    const req = new Request(path, init);

    fetch(req)
      .then(function (response: Response): Response {
        if (!response.ok) {
          throw Error(response.statusText);
        }
        return response;
      })
      .catch(function (error: Error): void {
        console.debug(error);
      });
  }

  public getHeaders(): Headers {
    const head = new Headers({
      'x-amzn-RequestId': uuid(),
      'Content-Type': 'application/octet-stream',
    });

    return head;
  }

  public static getCurrentDomain(): string {
    return document.domain;
  }

  public static getDefaultEndeavourEndpoint(): string {
    switch (FetchRequester.getCurrentDomain()) {
      case 'public.tableau.com': {
        return 'https://prod.telemetry.tableausoftware.com';
      }
      case 'public-stage.tableau.com': {
        return 'https://stage.telemetry.tableausoftware.com';
      }
      default: {
        return 'https://qa.telemetry.tableausoftware.com';
      }
    }
  }
}
